import * as React from "react";
import { css } from "@emotion/react";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  backgroundColor,
  textColor,
  borderColor,
  accentColor,
} from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { borderRadius } from "../../styles/base/borderStyle";

interface IProps extends React.AllHTMLAttributes<HTMLInputElement> {
  label?: string;
  value: string;
  onClick: any;
  styleKey?: any;
  borderStyleKey?: keyof IBorderStyle;
  borderRadiusStyleKey?: keyof IBorderRadiusStyle;
  fontStyleKey?: keyof IFontStyle;
  isFocus?: boolean;
}

export const FieldButton: React.FC<IProps> = (
  props: IProps
): React.ReactElement<IProps> => {
  const {
    label,
    value,
    onClick,
    styleKey = "normal",
    borderStyleKey = "default",
    borderRadiusStyleKey = "none",
    fontStyleKey = "default",
    isFocus,
  } = props;
  const showLabel = styleKey !== "noLabel";
  const labelStyles = isFocus ? labelFocusedStyle : labelStyle;
  return (
    <div
      css={[
        blockStyle[styleKey],
        borderStyle[borderStyleKey],
        borderRadiusStyle[borderRadiusStyleKey],
      ]}
      onClick={onClick}
    >
      {showLabel && label && <label css={labelStyles}>{label}</label>}
      <div css={[valueStyle, fontStyle[fontStyleKey]]}>{value}</div>
      {isFocus && <div css={indicatorStyle} />}
    </div>
  );
};

export interface IStyle {
  normal: any;
  noLabel: any;
}

export interface IBorderRadiusStyle {
  none: any;
  roundLeft: any;
  roundRight: any;
}

export interface IBorderStyle {
  default: any;
  noLeft: any;
  noRight: any;
}

export interface IFontStyle {
  default: any;
  small: any;
}

const blockBaseStyle = css`
  position: relative;
  padding: 0.25rem ${space.atom4x} 0.0625rem ${space.atom1_5x};
  box-sizing: border-box;
  margin: 0;
  width: 100%;
  height: 3.5rem;
  border: 1px solid ${borderColor.secondaryLightColor};
  background: ${backgroundColor.primaryLightColor};
  cursor: pointer;
  outline: none;
  transition: background-color 0.4s ease;
  &:hover {
    background: ${backgroundColor.secondaryLightColor};
  }
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    right: ${space.atom1_5x};
    width: 0.5rem;
    height: 0.5rem;
    border: 0.125rem solid ${borderColor.secondaryLightColor};
    border-top: 0;
    border-left: 0;
    transform: translateY(-70%) rotate(45deg);
  }
  ${moreThanBreakpoint("tablet")} {
    padding: 0.5rem ${space.atom4x} 0.0625rem ${space.atom1_5x};
    height: 4rem;
  }
  ${moreThanBreakpoint("desktop")} {
    padding-right: 2.5rem;
    padding-left: ${space.atom2x};
    &:after {
      right: ${space.atom2x};
    }
  }
`;
const blockStyle: IStyle = {
  normal: css`
    ${blockBaseStyle};
  `,
  noLabel: css`
    ${blockBaseStyle};
    display: flex;
    align-items: center;
  `,
};
const labelStyle = css`
  font-size: ${fontSize.smallest};
  line-height: ${lineHeight.caption3};
  color: ${textColor.secondaryDarkColor};
`;
const labelFocusedStyle = css`
  ${labelStyle};
  font-weight: ${fontWeight.medium};
  color: ${accentColor.secondaryColor};
`;
const valueStyle = css`
  font-size: ${fontSize.form};
  line-height: 1.5rem;
  font-weight: ${fontWeight.medium};
  color: ${textColor.primaryDarkColor};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${moreThanBreakpoint("tablet")} {
    line-height: 1.75rem;
  }
  ${moreThanBreakpoint("desktop")} {
    font-size: ${fontSize.mediumHeading};
  }
`;
const indicatorStyle = css`
  position: absolute;
  display: block;
  width: calc(100% - ${space.atom});
  height: 0.125rem;
  bottom: 0.25rem;
  left: 0.25rem;
  background-color: ${accentColor.secondaryColor};
`;

const borderRadiusStyle: IBorderRadiusStyle = {
  none: css`
    border-radius: 0;
  `,
  roundLeft: css`
    border-top-left-radius: ${borderRadius.normal};
    border-bottom-left-radius: ${borderRadius.normal};
  `,
  roundRight: css`
    border-top-right-radius: ${borderRadius.normal};
    border-bottom-right-radius: ${borderRadius.normal};
  `,
};

const borderStyle: IBorderStyle = {
  default: css``,
  noLeft: css`
    border-left: 0;
  `,
  noRight: css`
    border-right: 0;
  `,
};

const fontStyle: IFontStyle = {
  default: css``,
  small: css`
    font-size: ${fontSize.body};
    ${moreThanBreakpoint("desktop")} {
      font-size: 1rem;
    }
  `,
};
